import { useState, useEffect } from 'react';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { api, KycVerification } from '../../lib/api';

interface VerificationViewProps {
  onClose: () => void;
}

export function VerificationView({ onClose }: VerificationViewProps) {
  const [kyc, setKyc] = useState<KycVerification | null>(null);
  const [loadingKyc, setLoadingKyc] = useState(false);
  const [submittingKyc, setSubmittingKyc] = useState(false);
  const [kycError, setKycError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchKyc = async () => {
      try {
        setLoadingKyc(true);
        const profile = await api.profile.get();
        if (cancelled) return;
        
        try {
          const latest = await api.kyc.getLatest(profile.telegram_id);
          if (!cancelled) setKyc(latest);
        } catch {
          if (!cancelled) setKyc(null);
        }
      } catch (error) {
        console.error('Failed to load verification status:', error);
      } finally {
        if (!cancelled) setLoadingKyc(false);
      }
    };

    fetchKyc();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleStartKyc = async () => {
    try {
      setSubmittingKyc(true);
      setKycError(null);
      const profile = await api.profile.get();
      const v = await api.kyc.start(profile.telegram_id);
      setKyc(v);
    } catch (error) {
      console.error('Failed to start verification:', error);
      setKycError('Не вдалося запустити верифікацію. Спробуйте ще раз.');
    } finally {
      setSubmittingKyc(false);
    }
  };

  const handleUploadDocument = async (file: File) => {
    if (!kyc) return;
    try {
      setSubmittingKyc(true);
      setKycError(null);
      const updated = await api.kyc.uploadDocument(kyc.id, file);
      setKyc(updated);
    } catch (error) {
      console.error('Failed to upload document:', error);
      setKycError('Не вдалося завантажити документ.');
    } finally {
      setSubmittingKyc(false);
    }
  };

  const handleUploadSelfie = async (file: File) => {
    if (!kyc) return;
    try {
      setSubmittingKyc(true);
      setKycError(null);
      const updated = await api.kyc.uploadSelfie(kyc.id, file);
      setKyc(updated);
    } catch (error) {
      console.error('Failed to upload selfie:', error);
      setKycError('Не вдалося завантажити селфі.');
    } finally {
      setSubmittingKyc(false);
    }
  };

  const handleSubmitKyc = async () => {
    if (!kyc) return;
    try {
      setSubmittingKyc(true);
      setKycError(null);
      const updated = await api.kyc.submit(kyc.id);
      setKyc(updated);
    } catch (error) {
      console.error('Failed to submit verification:', error);
      setKycError('Не вдалося надіслати на перевірку.');
    } finally {
      setSubmittingKyc(false);
    }
  };

  const renderKycStatusLabel = () => {
    if (!kyc) return 'Не верифіковано';
    switch (kyc.status) {
      case 'pending':
        return 'Перевірка триває';
      case 'verified':
        return 'Верифіковано';
      case 'rejected':
        return 'Відхилено';
      default:
        return 'Не верифіковано';
    }
  };

  return (
    <div className="min-h-screen bg-splyt-gradient flex flex-col">
      {/* Header */}
      <div className="px-4 pt-4 pb-2 flex items-center gap-4 border-b border-white/10 bg-black/20 backdrop-blur-xl sticky top-0 z-10">
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full border backdrop-blur-xl flex items-center justify-center transition-all hover:border-white/30"
          style={{
            background: 'rgba(255, 255, 255, 0.05)',
            borderColor: 'rgba(255, 255, 255, 0.12)',
          }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'rgba(255, 255, 255, 0.7)' }} />
        </button>
        <h1 className="text-xl font-bold text-white">Верифікація</h1>
      </div>

      <div className="p-4 space-y-6 flex-1 overflow-y-auto">
        <div className="space-y-2">
          <p className="text-xs text-white/50 px-2">ВЕРИФІКАЦІЯ ОСОБИ</p>
          <div className="p-3 rounded-xl border space-y-3 bg-white/5 border-white/10">
            <div className={`w-full flex items-center justify-between rounded-lg px-1 py-1 text-left transition-colors ${!kyc && !loadingKyc ? 'bg-splyt-green/10' : ''}`}>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-splyt-green" />
                <span className="text-sm text-white">
                  Статус: {loadingKyc ? 'Завантаження...' : renderKycStatusLabel()}
                </span>
              </div>
            </div>

            {!kyc && !loadingKyc && (
              <>
                <p className="text-xs text-white/60">
                  Після верифікації ви зможете продавати послуги та файли, які не дають
                  миттєвого завантаження (наприклад, міксинг, PSD/Figma тощо).
                </p>
                <button
                  onClick={handleStartKyc}
                  disabled={submittingKyc}
                  className="w-full p-3 rounded-xl border flex items-center justify-center gap-2 transition-all hover:border-splyt-green/60 bg-splyt-green/10 border-splyt-green/50 text-splyt-green text-sm font-medium disabled:opacity-70"
                >
                  Почати верифікацію
                </button>
              </>
            )}

            {kyc && (
              <div className="space-y-3">
                <div className="space-y-1">
                  <p className="text-xs text-white/70">Крок 1: Завантажте фото документа</p>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={submittingKyc || kyc.status === 'verified'}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        void handleUploadDocument(file);
                      }
                    }}
                    className="w-full text-xs text-white/80 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border file:border-white/20 file:bg-white/10 file:text-xs file:text-white hover:file:border-splyt-green/50"
                  />
                  {kyc.has_document_image && (
                    <p className="text-xs text-splyt-green/80">✅ Документ завантажено</p>
                  )}
                </div>

                <div className="space-y-1">
                  <p className="text-xs text-white/70">Крок 2: Завантажте селфі</p>
                  <input
                    type="file"
                    accept="image/*"
                    disabled={submittingKyc || kyc.status === 'verified'}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        void handleUploadSelfie(file);
                      }
                    }}
                    className="w-full text-xs text-white/80 file:mr-2 file:py-1 file:px-2 file:rounded-lg file:border file:border-white/20 file:bg-white/10 file:text-xs file:text-white hover:file:border-splyt-green/50"
                  />
                  {kyc.has_selfie_image && (
                    <p className="text-xs text-splyt-green/80">✅ Селфі завантажено</p>
                  )}
                </div>

                <button
                  onClick={handleSubmitKyc}
                  disabled={submittingKyc || !kyc.has_document_image || !kyc.has_selfie_image || kyc.status === 'verified'}
                  className="w-full p-3 rounded-xl border flex items-center justify-center gap-2 transition-all hover:border-splyt-green/60 bg-splyt-green/10 border-splyt-green/50 text-splyt-green text-sm font-medium disabled:opacity-60"
                >
                  Надіслати на перевірку
                </button>

                {kyc.status === 'verified' && (
                  <p className="text-xs text-splyt-green/90">
                    🎉 Ваш акаунт верифіковано. Ви можете продавати складніші послуги та файли.
                  </p>
                )}

                {kyc.status === 'rejected' && kyc.reason && (
                  <p className="text-xs text-red-400/90">
                    Верифікацію відхилено: {kyc.reason}
                  </p>
                )}

                {kycError && (
                  <p className="text-xs text-red-400/90">{kycError}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}




